/**
 * Returns a deep clone of `value` with every string property trimmed of
 * leading and trailing whitespace. Recurses into plain objects and arrays;
 * non-string primitives (numbers, booleans, null, undefined) and class
 * instances (e.g. Date) are returned as-is.
 *
 * Intended for use as Mantine `useForm({ transformValues })` so a trailing
 * space in a text input never reaches Firestore — most importantly for email
 * fields, where a trailing space breaks downstream email delivery.
 */
export const trimStringsDeep = <T>(value: T): T => {
  if (typeof value === 'string') {
    return value.trim() as T;
  }
  if (Array.isArray(value)) {
    return value.map((item) => trimStringsDeep(item)) as T;
  }
  // Recurse only into plain objects. Class instances (Date, Timestamp, etc.)
  // are returned as-is so we don't shred their internal state by enumerating
  // properties Object.entries can't see.
  if (
    value !== null &&
    typeof value === 'object' &&
    Object.getPrototypeOf(value) === Object.prototype
  ) {
    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      result[key] = trimStringsDeep(val);
    }
    return result as T;
  }
  return value;
};
